import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const allServices = [
    { name: 'Web Development', slug: 'web-development' },
    { name: 'UI/UX Design', slug: 'ui-ux-design' },
    { name: 'Blockchain Development', slug: 'blockchain-development' },
    { name: 'E-commerce Development', slug: 'ecommerce-development' },
    { name: 'Custom Software Development', slug: 'custom-software-development' },
    { name: 'Mobile App Development', slug: 'mobile-app-development' },
    { name: '3D Animation', slug: '3d-animation' },
    { name: 'Cloud Solutions', slug: 'cloud-solutions' },
    { name: 'Digital Marketing', slug: 'digital-marketing' },
    { name: 'Creative Copywriting', slug: 'creative-copywriting' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_us-tech-services/artifacts/185hq6rh_Logo%20White.png" 
                alt="Code Commerce Logo" 
                className="h-20 md:h-24 w-auto"
              />
            </button>
            <p className="text-gray-400 leading-relaxed">
              Empowering businesses with innovative IT solutions and digital transformation expertise.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-blue-400">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => navigate('/')} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/about')} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => {
                  navigate('/');
                  setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                  Pricing
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/blog')} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                  Blog
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* All Services - Part 1 */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-blue-400">Our Services</h4>
            <ul className="space-y-3">
              {allServices.slice(0, 5).map((service) => (
                <li key={service.slug}>
                  <button 
                    onClick={() => navigate(`/service/${service.slug}`)}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 text-left"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* All Services - Part 2 */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-blue-400">More Services</h4>
            <ul className="space-y-3">
              {allServices.slice(5, 10).map((service) => (
                <li key={service.slug}>
                  <button 
                    onClick={() => navigate(`/service/${service.slug}`)}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 text-left"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info Section */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <h4 className="text-lg font-bold mb-6 text-blue-400 text-center">Contact Information</h4>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white mb-1">Address</p>
                <p className="text-gray-400 text-sm">12100 Wilshire Blvd, Los Angeles, CA 90025, USA</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white mb-1">Phone</p>
                <a href="tel:+18334009440" className="text-gray-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                  +1 (833) 400 9440
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white mb-1">Email</p>
                <a href="mailto:info@codecommerce.co" className="text-gray-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                  info@codecommerce.co
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} Code Commerce. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <button 
                onClick={() => navigate('/privacy-policy')}
                className="text-gray-400 hover:text-blue-400 text-sm transition-colors duration-300"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms-conditions')}
                className="text-gray-400 hover:text-blue-400 text-sm transition-colors duration-300"
              >
                Terms & Conditions
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;